import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getMyAdminStatus } from "@/lib/billing.functions";
import { getAdminDashboardKpis } from "@/lib/admin-dashboard.functions";
import { KpiTile } from "@/components/admin/kpi-tile";
import { StageCostChart } from "@/components/admin/stage-cost-chart";
import { NotificationsPanel } from "@/components/admin/notifications-panel";
import { FailuresTop10Panel } from "@/components/admin/failures-top10-panel";
import { ActivityGrid } from "@/components/admin/activity-grid";

export const Route = createFileRoute("/_authed/admin/dashboard")({
  head: () => ({
    meta: [
      { title: "Admin: Cost & observability: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    // Skip admin gate during SSR: Supabase session lives in localStorage,
    // so getMyAdminStatus would 401 server-side. Client-side gate runs
    // after hydration when the session is available. Matches _authed.tsx.
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: AdminDashboardPage,
});

const usd = (n: number) =>
  `$${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function AdminDashboardPage() {
  const fetchKpis = useServerFn(getAdminDashboardKpis);
  const q = useQuery({
    queryKey: ["admin-dashboard-kpis"],
    queryFn: () => fetchKpis({ data: { include_internal: false } }),
    refetchOnWindowFocus: false,
  });

  return (
    <main className="mx-auto max-w-[1080px] px-4 pb-16 pt-6">
      <header className="mb-6">
        <Link
          to="/admin"
          className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to admin
        </Link>
        <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          Cost &amp; observability.
        </h1>
        <p className="mt-1 text-[14px] text-brand-muted">
          Investor Readiness Report cost trend (30d) and pricing-lookup
          observability (7d). Pipeline cost not yet unified into a single
          view; tiles 1–4 cover IRR runs only.
        </p>
        <p className="mt-3 text-[12.5px] text-brand-muted">
          Internal and test activity is excluded.
        </p>
      </header>

      {q.isLoading && (
        <p className="text-[14px] text-brand-muted">Loading KPIs…</p>
      )}
      {q.error && (
        <p className="text-[14px] text-destructive">
          {q.error instanceof Error ? q.error.message : "Failed to load KPIs"}
        </p>
      )}

      {q.data && <KpiGrid data={q.data} />}

      <FailuresTop10Panel />

      <NotificationsPanel />
    </main>
  );
}

function KpiGrid({
  data,
}: {
  data: NonNullable<ReturnType<typeof useQuery<Awaited<ReturnType<typeof getAdminDashboardKpis>>>>["data"]>;
}) {
  const mix = data.stageMix30d;
  const mixTotal = mix.cost_stage_a + mix.cost_stage_b + mix.cost_stage_c;
  const pct = (n: number) =>
    mixTotal > 0 ? `${Math.round((n / mixTotal) * 100)}%` : "0%";

  const failTotal = data.pricingFailures7d.total;
  const failTone =
    failTotal === 0 ? "neutral" : failTotal < 5 ? "warn" : "bad";

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Tile 1 */}
        <KpiTile
          title="IRR cost (last 30d)"
          value={usd(data.deckStudioCost30d)}
          sublabel={`Across ${data.irrsCompleted30d + data.irrsFailed30d} report runs.`}
        />

        {/* Tile 2 */}
        <KpiTile
          title="Cost per IRR (avg)"
          value={usd(data.costPerIrrAvg30d ?? 0)}
          sublabel="Average over the last 30d."
        />

        {/* Tile 3 */}
        <KpiTile
          title="IRRs completed (30d)"
          value={String(data.irrsCompleted30d)}
          sublabel={
            data.irrsSuccessRate == null
              ? "Success rate: —"
              : `Success rate: ${Math.round(data.irrsSuccessRate * 100)}% (${data.irrsFailed30d} failed)`
          }
        />

        {/* Tile 4 */}
        <KpiTile
          title="Stage cost mix (30d)"
          value={usd(mixTotal)}
          sublabel={
            <span>
              A {pct(mix.cost_stage_a)} · B {pct(mix.cost_stage_b)} · C{" "}
              {pct(mix.cost_stage_c)}
            </span>
          }
        >
          {mixTotal > 0 && (
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-[oklch(0.6_0.15_250)]"
                style={{ width: `${(mix.cost_stage_a / mixTotal) * 100}%` }}
              />
              <div
                className="h-full bg-[oklch(0.65_0.15_180)]"
                style={{ width: `${(mix.cost_stage_b / mixTotal) * 100}%` }}
              />
              <div
                className="h-full bg-[oklch(0.7_0.15_100)]"
                style={{ width: `${(mix.cost_stage_c / mixTotal) * 100}%` }}
              />
            </div>
          )}
        </KpiTile>

        {/* Tile 5 */}
        <KpiTile
          title="Pricing lookup failures (7d)"
          value={`${failTotal} ${failTotal === 1 ? "failure" : "failures"}`}
          sublabel={
            failTotal === 0
              ? "No missing pricing rows in the last 7 days."
              : `Pipeline: ${data.pricingFailures7d.pipeline} · IRR: ${data.pricingFailures7d.deck_studio}`
          }
          badge={failTotal === 0 ? "clean" : String(failTotal)}
          tone={failTone}
        />

        {/* Tile 6 */}
        <KpiTile
          title="Models without pricing row"
          value={
            data.unpricedModels.length === 0
              ? "—"
              : String(data.unpricedModels.length)
          }
          sublabel={
            data.unpricedModels.length === 0
              ? "All models priced."
              : data.unpricedModels.join(", ")
          }
          badge={data.unpricedModels.length === 0 ? undefined : "action"}
          tone={data.unpricedModels.length === 0 ? "neutral" : "bad"}
        />
      </div>

      {/* 30-day activity panel — embedded above the cost chart per Phase 2 spec. */}
      <ActivityGrid />

      {/* Trend chart */}
      <section className="mt-8 rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="mb-4 font-serif text-[20px] text-brand-ink">
          Daily cost by stage (30d)
        </h2>
        <StageCostChart data={data.dailyTrend30d} />
      </section>
    </>
  );
}